
// There are 3 statuses for a patient account:
// - Connected - the patient account is fully authorized and Fasten can access medical records from this institution
// - Pending - the patient has specified they have records at this institution, but Fasten has not yet been able to access them.
//             the patient must complete the connection process by logging into their account at the institution.
// - Discovered - these are facilities that have been found via a record locator search. The patient has not yet agreed to share medical records from this institution.
//             - this status is similar to "pending" for display purposes.
import {
  LighthouseBrandListDisplayItem,
  LighthouseEndpointListDisplayItem,
  LighthousePortalListDisplayItem,
  VaultProfile,
  ConnectedPatientAccount, CspType, PatientDemographics, RecordLocatorFacilityConnected, SourceCredentialType
} from "@shared-library";

export class VaultProfileConfig {
  vaultProfile?: VaultProfile;
  email?: string; //patient email address; if set will be used to prepopulate support forms and TEFCA mode signin
  identityVerificationFailureCount?: number;
  // Tracks whether identity verification was completed or explicitly skipped
  // for the current in-memory sign-in session.
  identityVerificationHandledForSession?: boolean;
  verifiedIdentityCspType?: CspType; //the CSP type selected by the patient during identity verification (or previously authenticated CSP type if identity verification is still active)
  verifiedIdentityPatientDemographics?: PatientDemographics //the patient demographics used in the TEFCA search


  // used to indicate that the record locator search has completed, and records are available in the pendingPatientAccounts
  // and connectedPatientAccounts
  rlsQueryComplete?: boolean;

  // connected patient accounts (Smart-on-FHIR, TEFCA-facilitated or TEFCA-direct results)
  // patient has agreed to share medical records from these institutions.
  connectedPatientAccounts?: ConnectedPatientAccount[];

  // pending patient accounts (Smart-on-FHIR or TEFCA-facilitated record locator search results)
  // patient has not yet agreed to share medical records from these institutions.
  pendingPatientAccounts?: {
    [key:string]: {
      vault_profile_connection_id?: string, //only used for tefca-facilitated record locator search results

      brand: LighthouseBrandListDisplayItem,
      portal: LighthousePortalListDisplayItem,
      endpoint: LighthouseEndpointListDisplayItem
    }
  };

  //discovered patient accounts (TEFCA-direct record locator search results)
  // patient has not yet agreed to share medical records from these institutions.
  discoveredPatientAccounts?: {
    [key:string]: {
      vault_profile_connection_id?: string, //only used for tefca-facilitated record locator search results

      brand: LighthouseBrandListDisplayItem,
      portal: LighthousePortalListDisplayItem,
      endpoint: LighthouseEndpointListDisplayItem,
      patient_auth_type?: SourceCredentialType, //the type of authorization the patient must complete to connect this account. This will be used to determine the appropriate messaging and connection flow for the patient.
    }
  };


  addPendingAccount(externalState: string, brand: LighthouseBrandListDisplayItem, portal: LighthousePortalListDisplayItem, endpoint: LighthouseEndpointListDisplayItem, vaultProfileConnectionId?: string): void {
    if (!this.pendingPatientAccounts) {
      this.pendingPatientAccounts = {}
    }
    this.pendingPatientAccounts[externalState] = {brand, portal, endpoint, vault_profile_connection_id: vaultProfileConnectionId}
  }

  //discovered accounts do not require a unique external state for matching, because they already have a unique identifier (vaultProfileConnectionId)
  addDiscoveredAccount(brand: LighthouseBrandListDisplayItem, portal: LighthousePortalListDisplayItem, endpoint: LighthouseEndpointListDisplayItem, vaultProfileConnectionId: string, patientAuthorizationType: SourceCredentialType = SourceCredentialType.SourceCredentialTypeTefcaDirect): void {
    if (!this.discoveredPatientAccounts) {
      this.discoveredPatientAccounts = {}
    }
    this.discoveredPatientAccounts[vaultProfileConnectionId] = {brand, portal, endpoint, vault_profile_connection_id: vaultProfileConnectionId, patient_auth_type: patientAuthorizationType}
  }

  addConnectedAccount(
    external_state: string,
    org_connection_id: string,
    connection_status: string,
    platform_type: string,
    brand_id?: string,
    portal_id?: string,
    endpoint_id?: string,
    vault_profile_connection_id?: string,
    patient_auth_type?: string,
    scope?: string,
    consent_expires_at?: string,
    tefca_directory_id?: string,
    display_name?: string,
  ): void {
    if (!this.connectedPatientAccounts) {
      this.connectedPatientAccounts = []
    }

    //find the newly connected account metadata from the pending list and move it to the connected list
    let foundPendingPatientAccount = this.pendingPatientAccounts?.[external_state]
    let foundDiscoveredPatientAccount = this.discoveredPatientAccounts?.[external_state]

    if (foundPendingPatientAccount) {
      delete this.pendingPatientAccounts![external_state]
      this.upsertConnectedAccount({
        org_connection_id,
        connection_status,
        platform_type,
        brand: foundPendingPatientAccount!.brand,
        portal: foundPendingPatientAccount!.portal,
        endpoint: foundPendingPatientAccount!.endpoint,
        vault_profile_connection_id: vault_profile_connection_id,
        patient_auth_type: patient_auth_type,
        scope: scope,
        consent_expires_at: consent_expires_at,
        tefca_directory_id: tefca_directory_id,
      })
    } else if (foundDiscoveredPatientAccount){
      delete this.discoveredPatientAccounts![external_state]
      this.upsertConnectedAccount({
        org_connection_id,
        connection_status,
        platform_type,
        brand: foundDiscoveredPatientAccount!.brand,
        portal: foundDiscoveredPatientAccount!.portal,
        endpoint: foundDiscoveredPatientAccount!.endpoint,
        vault_profile_connection_id: vault_profile_connection_id,
        patient_auth_type: patient_auth_type,
        tefca_directory_id: tefca_directory_id,
      })
    } else {
      //TODO: lookup the brand, portal, endpoing information by ID from the Lighthouse API, and add it to the connected Patients account list.
      console.warn("we may not know the brand, portal, endpoint information, so generating it with placeholders. Most likely this is a reconnect operation.")
      console.warn("pendingAccounts", this.pendingPatientAccounts, "connectionParams", org_connection_id, connection_status, platform_type, brand_id, portal_id, endpoint_id)
      this.upsertConnectedAccount({
        org_connection_id,
        connection_status,
        platform_type,
        vault_profile_connection_id: vault_profile_connection_id,
        patient_auth_type: patient_auth_type,
        brand: {id: brand_id!, name: display_name || 'unknown', portals:[], hidden: false, last_updated: '', portal_ids: [portal_id!]},
        portal: {id: portal_id!, last_updated: '', endpoint_ids: [endpoint_id!], name: display_name || 'unknown' },
        endpoint: {id: endpoint_id!, platform_type: platform_type},
        scope: scope,
        consent_expires_at: consent_expires_at,
        tefca_directory_id: tefca_directory_id,
      })
    }
  }

  addConnectedRecordLocatorAccount(recordLocatorFacilityConnected: RecordLocatorFacilityConnected, vault_profile_connection_id: string): void {
    if (!this.connectedPatientAccounts) {
      this.connectedPatientAccounts = []
    }

    this.upsertConnectedAccount({
      org_connection_id: recordLocatorFacilityConnected.org_connection_id,
      connection_status: 'connected',
      platform_type: 'tefca',

      brand: recordLocatorFacilityConnected.brand,
      portal: recordLocatorFacilityConnected.portal,
      endpoint: recordLocatorFacilityConnected.endpoint,
      vault_profile_connection_id: vault_profile_connection_id,
      patient_auth_type: recordLocatorFacilityConnected.patient_authorization_type,
      scope: '',
      consent_expires_at: '',
      tefca_directory_id: recordLocatorFacilityConnected.facility_id,
    })
  }

  // this is used when we successfully generated an org_connection_id for a TEFCA Direct account.
  authorizeTefcaDirectConnectedAccount(vaultProfileConnectionId: string, orgConnectionId: string, connectionStatus: string, tefcaDirectoryId?: string): boolean {
    let ndx = this.connectedPatientAccounts?.findIndex((acc) => acc.vault_profile_connection_id === vaultProfileConnectionId)
    if (ndx === undefined || ndx < 0){
      return false
    }
    let existingAccount = this.connectedPatientAccounts![ndx]
    existingAccount.connection_status = connectionStatus
    existingAccount.org_connection_id = orgConnectionId
    if (tefcaDirectoryId) {
      existingAccount.tefca_directory_id = tefcaDirectoryId
    }
    this.connectedPatientAccounts![ndx] = existingAccount
    return true
  }

  //this is used when we were unable to generate an org_connection_id for a TEFCA Direct account.
  revokeTefcaDirectConnectedAccount(vaultProfileConnectionId: string): boolean {
    let ndx = this.connectedPatientAccounts?.findIndex((acc) => acc.vault_profile_connection_id === vaultProfileConnectionId)
    if (ndx === undefined || ndx < 0){
      return false
    }

    //delete the entry from connectedPatientAccounts
    this.connectedPatientAccounts!.splice(ndx, 1)
    return true
  }

  deselectTefcaDirectConnectedAccount(vaultProfileConnectionId: string): boolean {
    const accountIndex = this.connectedPatientAccounts?.findIndex(
      (account) => account.vault_profile_connection_id === vaultProfileConnectionId,
    )
    if (accountIndex === undefined || accountIndex < 0) {
      return false
    }

    const account = this.connectedPatientAccounts![accountIndex]
    if (!account.brand || !account.portal || !account.endpoint) {
      return false
    }

    this.addDiscoveredAccount(
      account.brand,
      account.portal,
      account.endpoint,
      vaultProfileConnectionId,
      account.patient_auth_type as SourceCredentialType,
    )
    this.connectedPatientAccounts!.splice(accountIndex, 1)
    return true
  }

  private upsertConnectedAccount(account: ConnectedPatientAccount): void {
    if (!this.connectedPatientAccounts) {
      this.connectedPatientAccounts = [];
    }

    const existingIndex = this.connectedPatientAccounts.findIndex((existingAccount) => {
      if (
        account.vault_profile_connection_id &&
        existingAccount.vault_profile_connection_id === account.vault_profile_connection_id
      ) {
        return true;
      }

      return !!account.org_connection_id && existingAccount.org_connection_id === account.org_connection_id;
    });

    if (existingIndex >= 0) {
      this.connectedPatientAccounts[existingIndex] = {
        ...this.connectedPatientAccounts[existingIndex],
        ...account,
      };
      return;
    }

    this.connectedPatientAccounts.push(account);
  }

}
